import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import { UserCheck, CheckCircle, XCircle, Clock, AlertCircle, FileText, Shield, Syringe } from 'lucide-react';

interface ConsultantProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  created_at: string;
}

interface OnboardingRecord {
  id: string;
  consultant_id: string;
  references_status: 'pending' | 'verified' | 'rejected';
  references_checked_by: string | null;
  references_checked_at: string | null;
  references_notes: string | null;
  background_check_status: 'pending' | 'completed' | 'failed';
  background_check_completed_by: string | null;
  background_check_completed_at: string | null;
  background_check_notes: string | null;
  drug_screening_status: 'pending' | 'completed' | 'failed';
  drug_screening_completed_by: string | null;
  drug_screening_completed_at: string | null;
  drug_screening_notes: string | null;
  onboarding_completed: boolean;
  created_at: string;
  updated_at: string;
}

interface ConsultantWithOnboarding extends ConsultantProfile {
  onboarding?: OnboardingRecord;
  downloads?: {
    onboarding_guide: boolean;
    timesheet_guidelines: boolean;
  };
}

export default function ConsultantOnboarding() {
  const navigate = useNavigate();
  const [consultants, setConsultants] = useState<ConsultantWithOnboarding[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConsultant, setSelectedConsultant] = useState<ConsultantWithOnboarding | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'completed'>('all');

  const [modalData, setModalData] = useState({
    references_status: 'pending' as 'pending' | 'verified' | 'rejected',
    references_notes: '',
    background_check_status: 'pending' as 'pending' | 'completed' | 'failed',
    background_check_notes: '',
    drug_screening_status: 'pending' as 'pending' | 'completed' | 'failed',
    drug_screening_notes: ''
  });

  useEffect(() => {
    checkAuth();
    fetchConsultants();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
    }
  };

  const fetchConsultants = async () => {
    try {
      setLoading(true);

      const { data: profiles, error: profilesError } = await supabase
        .from('consultant_profiles')
        .select('id, first_name, last_name, email, phone, created_at')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      if (!profiles || profiles.length === 0) {
        setConsultants([]);
        setLoading(false);
        return;
      }

      const consultantIds = profiles.map(p => p.id);

      const { data: onboardingRecords, error: onboardingError } = await supabase
        .from('consultant_onboarding')
        .select('*')
        .in('consultant_id', consultantIds);

      if (onboardingError) throw onboardingError;

      const { data: downloadRecords, error: downloadError } = await supabase
        .from('consultant_document_downloads')
        .select('consultant_id, document_type')
        .in('consultant_id', consultantIds);

      if (downloadError) throw downloadError;

      const enrichedProfiles = profiles.map(profile => {
        const onboarding = onboardingRecords?.find(o => o.consultant_id === profile.id);
        const downloads = downloadRecords?.filter(d => d.consultant_id === profile.id) || [];
        const downloadStatus = {
          onboarding_guide: downloads.some(d => d.document_type === 'onboarding_guide'),
          timesheet_guidelines: downloads.some(d => d.document_type === 'timesheet_guidelines')
        };

        return {
          ...profile,
          onboarding,
          downloads: downloadStatus
        };
      });

      setConsultants(enrichedProfiles);
    } catch (error) {
      console.error('Error fetching consultants:', error);
      showMessage('error', 'Failed to load consultants');
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (consultant: ConsultantWithOnboarding) => {
    setSelectedConsultant(consultant);

    if (consultant.onboarding) {
      setModalData({
        references_status: consultant.onboarding.references_status,
        references_notes: consultant.onboarding.references_notes || '',
        background_check_status: consultant.onboarding.background_check_status,
        background_check_notes: consultant.onboarding.background_check_notes || '',
        drug_screening_status: consultant.onboarding.drug_screening_status,
        drug_screening_notes: consultant.onboarding.drug_screening_notes || ''
      });
    } else {
      setModalData({
        references_status: 'pending',
        references_notes: '',
        background_check_status: 'pending',
        background_check_notes: '',
        drug_screening_status: 'pending',
        drug_screening_notes: ''
      });
    }

    setShowModal(true);
  };

  const handleSaveOnboarding = async () => {
    if (!selectedConsultant) return;

    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const onboardingCompleted =
        modalData.references_status === 'verified' &&
        modalData.background_check_status === 'completed' &&
        modalData.drug_screening_status === 'completed';

      const updateData: any = {
        consultant_id: selectedConsultant.id,
        references_status: modalData.references_status,
        references_notes: modalData.references_notes || null,
        background_check_status: modalData.background_check_status,
        background_check_notes: modalData.background_check_notes || null,
        drug_screening_status: modalData.drug_screening_status,
        drug_screening_notes: modalData.drug_screening_notes || null,
        onboarding_completed: onboardingCompleted,
        updated_at: new Date().toISOString()
      };

      if (selectedConsultant.onboarding?.references_status !== modalData.references_status &&
          modalData.references_status === 'verified') {
        updateData.references_checked_by = user?.id;
        updateData.references_checked_at = new Date().toISOString();
      }

      if (selectedConsultant.onboarding?.background_check_status !== modalData.background_check_status &&
          modalData.background_check_status === 'completed') {
        updateData.background_check_completed_by = user?.id;
        updateData.background_check_completed_at = new Date().toISOString();
      }

      if (selectedConsultant.onboarding?.drug_screening_status !== modalData.drug_screening_status &&
          modalData.drug_screening_status === 'completed') {
        updateData.drug_screening_completed_by = user?.id;
        updateData.drug_screening_completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('consultant_onboarding')
        .upsert(updateData);

      if (error) throw error;

      showMessage('success', 'Onboarding status updated successfully');
      setShowModal(false);
      await fetchConsultants();
    } catch (error) {
      console.error('Error updating onboarding:', error);
      showMessage('error', 'Failed to update onboarding status');
    } finally {
      setProcessing(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getStatusBadge = (status: string, type: 'references' | 'background' | 'drug') => {
    if (status === 'verified' || status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3" />
          {type === 'references' ? 'Verified' : 'Completed'}
        </span>
      );
    }
    if (status === 'rejected' || status === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="h-3 w-3" />
          {type === 'references' ? 'Rejected' : 'Failed'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock className="h-3 w-3" />
        Pending
      </span>
    );
  };

  const filteredConsultants = consultants
    .filter(consultant => {
      if (filterStatus === 'completed') {
        return consultant.onboarding?.onboarding_completed === true;
      }
      if (filterStatus === 'pending') {
        return !consultant.onboarding?.onboarding_completed;
      }
      return true;
    })
    .filter(consultant => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${consultant.first_name} ${consultant.last_name}`.toLowerCase();
      const email = consultant.email.toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Consultant Onboarding</h1>
            <p className="text-gray-600">Track references, background checks, and drug screening status</p>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search consultants by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-gray-900 bg-white"
              />
            </div>
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'pending' | 'completed')}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white"
              >
                <option value="all">All Consultants</option>
                <option value="pending">Pending Onboarding</option>
                <option value="completed">Completed Onboarding</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Consultants ({filteredConsultants.length})
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center text-gray-600">Loading consultants...</div>
            ) : filteredConsultants.length === 0 ? (
              <div className="p-8 text-center text-gray-600">
                No consultants found.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredConsultants.map((consultant) => (
                  <div key={consultant.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-[#40E0D0] bg-opacity-10 flex items-center justify-center">
                            <UserCheck className="h-6 w-6 text-[#40E0D0]" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {consultant.first_name} {consultant.last_name}
                            </h3>
                            <p className="text-sm text-gray-600">{consultant.email}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-600 mb-1">Onboarding Guide</p>
                              {consultant.downloads?.onboarding_guide ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3" />
                                  Downloaded
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <Clock className="h-3 w-3" />
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-600 mb-1">Timesheet Guide</p>
                              {consultant.downloads?.timesheet_guidelines ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3" />
                                  Downloaded
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <Clock className="h-3 w-3" />
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-600 mb-1">References</p>
                              {getStatusBadge(
                                consultant.onboarding?.references_status || 'pending',
                                'references'
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Shield className="h-5 w-5 text-gray-400" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-600 mb-1">Background Check</p>
                              {getStatusBadge(
                                consultant.onboarding?.background_check_status || 'pending',
                                'background'
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Syringe className="h-5 w-5 text-gray-400" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-600 mb-1">Drug Screening</p>
                              {getStatusBadge(
                                consultant.onboarding?.drug_screening_status || 'pending',
                                'drug'
                              )}
                            </div>
                          </div>
                        </div>

                        {consultant.onboarding?.onboarding_completed && (
                          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2 text-green-800">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-sm font-medium">Onboarding Completed</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => openModal(consultant)}
                        className="ml-4 px-4 py-2 bg-[#40E0D0] text-white rounded-lg hover:bg-[#40E0D0]/90 text-sm"
                      >
                        Manage Onboarding
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && selectedConsultant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Onboarding: {selectedConsultant.first_name} {selectedConsultant.last_name}
              </h2>
              <p className="text-sm text-gray-600 mt-1">{selectedConsultant.email}</p>
            </div>

            <div className="p-6 space-y-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-[#40E0D0]" />
                  <h3 className="text-lg font-semibold text-gray-900">References</h3>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={modalData.references_status}
                      onChange={(e) => setModalData({
                        ...modalData,
                        references_status: e.target.value as 'pending' | 'verified' | 'rejected'
                      })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white"
                    >
                      <option value="pending">Pending</option>
                      <option value="verified">Verified</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={modalData.references_notes}
                      onChange={(e) => setModalData({
                        ...modalData,
                        references_notes: e.target.value
                      })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white"
                      rows={3}
                      placeholder="Add notes about reference verification..."
                    />
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-5 w-5 text-[#40E0D0]" />
                  <h3 className="text-lg font-semibold text-gray-900">Background Check</h3>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={modalData.background_check_status}
                      onChange={(e) => setModalData({
                        ...modalData,
                        background_check_status: e.target.value as 'pending' | 'completed' | 'failed'
                      })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white"
                    >
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={modalData.background_check_notes}
                      onChange={(e) => setModalData({
                        ...modalData,
                        background_check_notes: e.target.value
                      })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white"
                      rows={3}
                      placeholder="Add notes about background check..."
                    />
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Syringe className="h-5 w-5 text-[#40E0D0]" />
                  <h3 className="text-lg font-semibold text-gray-900">Drug Screening</h3>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={modalData.drug_screening_status}
                      onChange={(e) => setModalData({
                        ...modalData,
                        drug_screening_status: e.target.value as 'pending' | 'completed' | 'failed'
                      })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white"
                    >
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={modalData.drug_screening_notes}
                      onChange={(e) => setModalData({
                        ...modalData,
                        drug_screening_notes: e.target.value
                      })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white"
                      rows={3}
                      placeholder="Add notes about drug screening..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-900"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOnboarding}
                disabled={processing}
                className="px-4 py-2 bg-[#40E0D0] text-white rounded-lg hover:bg-[#40E0D0]/90 disabled:opacity-50"
              >
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
